import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import { messages } from '../messages';
import style from './payment-method-section.module.less';

export const NEW_PAYMENT_METHOD_VALUE = 'NEW_PAYMENT_METHOD' as const;

type StoredPaymentMethod = {
  id: string;
  nickname?: string | null;
  accountHolderName?: string;
  lastFourDigits?: string;
  brand?: string;
};

type AuctanePayAccountSelectProps = {
  value: string;
  onChange: (accountId: string) => void;
  disabled?: boolean;
  showError?: boolean;
  errorMessage?: string;
};

const formatAccountLabel = (account: StoredPaymentMethod): string => {
  // If nickname exists, use it as the primary display
  if (account.nickname) {
    return account.nickname;
  }

  // Fallback to building label from other fields
  const parts: string[] = [];

  if (account.accountHolderName) {
    parts.push(account.accountHolderName);
  }

  if (account.brand) {
    parts.push(account.brand);
  }

  if (account.lastFourDigits) {
    parts.push(`****${account.lastFourDigits}`);
  }

  return parts.join(' - ') || account.id;
};

export const AuctanePayAccountSelect = ({
  value,
  onChange,
  disabled = false,
  showError = false,
  errorMessage,
}: AuctanePayAccountSelectProps) => {
  const intl = useIntl();
  const { paymentMethodDetails, isPaymentSummaryListLoading } = usePaymentAndSubscriptionMegaContext();

  if (isPaymentSummaryListLoading) {
    return (
      <FlexColumn gap={Gap.Space16}>
        <Text textType={TextType.H4} value={messages.paymentMethodSectionTitle} />
        <FlexColumn gap={Gap.Space8}>
          <Text textType={TextType.Label} value={messages.auctanePayAccountLabel} />
          <LoadingIndicator />
        </FlexColumn>
      </FlexColumn>
    );
  }

  const placeholder = intl.formatMessage(messages.auctanePayAccountPlaceholder);

  // Only include existing payment methods in dropdown (no "Add new" option)
  const options = paymentMethodDetails.map((account) => ({
    value: account.id,
    label: formatAccountLabel(account),
  }));

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.label || placeholder;

  const handleAddNewClick = () => {
    onChange(NEW_PAYMENT_METHOD_VALUE);
  };

  return (
    <FlexColumn gap={Gap.Space16}>
      <Text textType={TextType.H4} value={messages.paymentMethodSectionTitle} />
      <FlexRow gap={Gap.Space12}>
        <DropdownMenu
          className={style['dropdown-column']}
          disabled={disabled}
          hasError={showError && !!errorMessage}
          inlineMessage={showError && errorMessage ? errorMessage : undefined}
          inlineMessageType={showError && errorMessage ? INLINE_MESSAGE_TYPES.error : undefined}
          variant='form'
        >
          <DropdownMenuToggler>{displayValue}</DropdownMenuToggler>
          {options.map((option) => (
            <DropdownMenuItem key={option.value} onClick={() => onChange(option.value)}>
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        <Button
          ariaLabel={intl.formatMessage(messages.addNewPaymentMethodOption)}
          disabled={disabled}
          onClick={handleAddNewClick}
          theme={THEME_CONSTANTS.link}
        >
          {intl.formatMessage(messages.addNewPaymentMethodOption)}
        </Button>
      </FlexRow>
    </FlexColumn>
  );
};
